// React hooks
import { useRef, useState } from "react";

// PrimeReact components
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

// Toolkit imports
import { submitFeedback } from "sj-ui-toolkit/src/api";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { PageBreadcrumb } from "sj-ui-toolkit/src/atlas/components";

/**
 * FeedbackPage Component
 *
 * Provides a form for users to submit feedback including:
 * - Detailed feedback text
 * - Areas for improvement
 * - Optional file attachments (screenshots, documents)
 *
 * Features:
 * - Form validation for required fields
 * - File upload with size and type restrictions
 * - Success/error toast notifications
 * - Form reset after successful submission
 */
const FeedbackPage = ({ forQuiz = false }) => {
  useBreadcrumb({ label: "Submit Feedback", route: "/feedback", display: "page" });

  const fileUploadRef = useRef(null);

  const feedbackTypeOptions = [
    { label: "Bug Report", value: "bug_report" },
    { label: "Feature Request", value: "feature_request" },
    { label: "User Experience", value: "user_experience" },
    { label: "Performance Issue", value: "performance_issue" },
    { label: "Content Quality", value: "content_quality" },
    { label: "Documentation", value: "documentation" },
    { label: "Quiz Question Issue", value: "quiz_question" },
    { label: "Other", value: "other" },
  ];

  const [details, setDetails] = useState("");
  const [feedbackType, setFeedbackType] = useState(forQuiz ? "quiz_question" : null);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleFileSelect = e => {
    if (e.files && e.files[0]) {
      setFile(e.files[0]);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!details.trim() || !feedbackType) {
      return;
    }

    try {
      setIsSubmitting(true);
      await submitFeedback({
        details,
        feedbackType,
        file,
      });

      setIsSuccess(true);

      // Reset form
      setDetails("");
      setFeedbackType(forQuiz ? "quiz_question" : null);
      setFile(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }

      // Reset success message after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={C(T.largePaddedContainer, "h-full")} aria-label="Feedback">
      {!forQuiz && <PageBreadcrumb />}
      <div className="mb-6 max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Submit Feedback</h1>
          <p className={T.textMuted}>
            We value your feedback! Please share your thoughts, suggestions, or report any issues
            you've encountered.
          </p>
        </div>

        {isSuccess && (
          <Message
            severity="success"
            text="Your feedback has been successfully submitted. Thank you!"
            className="mb-4 w-full"
          />
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {!forQuiz && (
            <div className="flex flex-col gap-2">
              <label htmlFor="feedbackType" className="font-semibold">
                How can we improve? <span className="error-text">*</span>
              </label>
              <Dropdown
                id="feedbackType"
                value={feedbackType}
                onChange={e => setFeedbackType(e.value)}
                options={feedbackTypeOptions}
                placeholder="Please select an option"
                className="w-full"
                disabled={forQuiz}
                aria-required="true"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label htmlFor="details" className="font-semibold">
              Details <span className="error-text">*</span>
            </label>
            <InputTextarea
              id="details"
              value={details}
              onChange={e => setDetails(e.target.value)}
              rows={5}
              placeholder="Please provide detailed information about your feedback..."
              className="w-full"
              required
              aria-required="true"
            />
          </div>

          <div className="flex gap-5">
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              name="file"
              accept="image/*,.pdf,.doc,.docx"
              maxFileSize={10000000}
              onSelect={handleFileSelect}
              auto={false}
              chooseLabel="Choose File"
              pt={{
                input: {
                  id: "feedback-file-upload",
                  "aria-label": "File upload for feedback attachment",
                },
              }}
              severity="secondary"
            />
            <div>
              <label htmlFor="feedback-file-upload" className="font-semibold">
                Attachment (optional)
              </label>
              <p className={C(T.textMuted, "text-sm mb-2")}>
                You can attach a screenshot or file to help illustrate your feedback
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              type="submit"
              label="Submit Feedback"
              icon="pi pi-send"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
            <Button
              type="button"
              label="Clear"
              icon="pi pi-times"
              severity="secondary"
              onClick={() => {
                setDetails("");
                setFeedbackType(forQuiz ? "quiz_question" : null);
                setFile(null);
                if (fileUploadRef.current) {
                  fileUploadRef.current.clear();
                }
              }}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackPage;
